import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { EmailReportDialog } from "@/components/EmailReportDialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { HealthBadge } from "@/components/HealthBadge";
import { HardSoftBreakdown } from "@/components/HardSoftBreakdown";
import { Gauge, MapPin } from "@phosphor-icons/react";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import InviteTeammateNudge from "@/components/InviteTeammateNudge";
import { Warning, TrendUp, TrendDown, Wallet, Buildings, UsersThree, ClockCounterClockwise, PlusCircle, PencilSimple, Trash, ArrowsLeftRight, PaperPlaneTilt, UserPlus, ArrowRight, ChartLineUp, CurrencyDollar, DownloadSimple, FilePdf, Receipt, Check } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend,
  AreaChart, Area,
} from "recharts";

const PIE_COLORS = ["#0A0A0A", "#5E6470", "#00875A", "#F59E0B", "#E11D48", "#94a3b8", "#334155", "#cbd5e1"];

const REPORT_KIND_LABEL = {
  team_digest: "Team digest",
  client_digest: "Client snapshot",
  portfolio_report: "Portfolio report",
  category_report: "Category report",
  tax_report: "Tax report",
};

const ACT_ICON = (a) => {
  if (a?.startsWith("created") || a?.startsWith("added")) return PlusCircle;
  if (a?.startsWith("updated")) return PencilSimple;
  if (a?.startsWith("deleted")) return Trash;
  if (a?.includes("converted")) return ArrowsLeftRight;
  if (a?.includes("emailed")) return PaperPlaneTilt;
  if (a?.startsWith("invited") || a?.startsWith("joined")) return UserPlus;
  return ClockCounterClockwise;
};
const ACT_COLOR = (a) => {
  if (a?.startsWith("deleted")) return "text-destructive";
  if (a?.startsWith("created") || a?.startsWith("added") || a?.startsWith("joined")) return "text-emerald-700";
  return "text-muted-foreground";
};
function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Stat({ label, value, sub, icon: Icon, danger, testId, onClick }) {
  return (
    <div data-testid={testId} onClick={onClick}
      className={`border-r border-b border-border p-5 sm:p-6 flex flex-col justify-between min-h-[112px] sm:min-h-[130px] transition-colors duration-200 hover:bg-secondary/40 ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-center justify-between">
        <p className="label-caps">{label}</p>
        <Icon size={18} className={danger ? "text-destructive" : "text-muted-foreground"} />
      </div>
      <div>
        <div className={`font-mono-fig text-2xl sm:text-3xl font-semibold tabular-nums ${danger ? "text-destructive" : ""}`}>
          {value}
        </div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [tax, setTax] = useState(null);
  const [sending, setSending] = useState(false);
  const [driftBasis, setDriftBasis] = useState("actual");
  const [leaderFilter, setLeaderFilter] = useState("all");
  const [showLandItems, setShowLandItems] = useState(false);
  const [landItems, setLandItems] = useState(null);
  const [landEmailOpen, setLandEmailOpen] = useState(false);
  const [portfolioEmailOpen, setPortfolioEmailOpen] = useState(false);
  const [taxEmailOpen, setTaxEmailOpen] = useState(false);
  const [emailLog, setEmailLog] = useState(null);
  const [showSendHistory, setShowSendHistory] = useState(false);
  const { user } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const navigate = useNavigate();

  const loadEmailLog = () => { if (isManager) api.get("/team/email-log").then((r) => setEmailLog(r.data)).catch(() => {}); };

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/activity?limit=6").then((r) => setActivity(r.data)).catch(() => {});
    api.get("/invoices/tax-summary").then((r) => setTax(r.data)).catch(() => {});
    loadEmailLog();
  }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/reports/send-digest");
      if (res.sent > 0) toast.success(res.message);
      else toast.message("Digest generated — email delivery is limited to verified addresses in testing mode.");
    } catch (e) { toast.error("Could not send digest"); }
    finally { setSending(false); }
  };

  const downloadPortfolio = async (fmt) => {
    try {
      const res = await api.get(`/reports/portfolio-cost-report.${fmt}`, { responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `portfolio_cost_report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download report"); }
  };

  const acknowledgeAlert = async (id) => {
    try {
      await api.post(`/line-items/${id}/acknowledge-alert`);
      const { data: fresh } = await api.get("/dashboard");
      setData(fresh);
      toast.success("Alert acknowledged — it'll reappear if the cost rises further");
    } catch { toast.error("Could not acknowledge"); }
  };

  const openLandItems = async () => {
    setShowLandItems(true);
    setLandItems(null);
    try {
      const { data: res } = await api.get("/categories/line-items", { params: { name: "Land Acquisition" } });
      setLandItems(res.items || []);
    } catch { setLandItems([]); }
  };

  const downloadCategoryItems = async (fmt) => {
    try {
      const res = await api.get(`/categories/line-items/export.${fmt}`, {
        params: { name: "Land Acquisition" }, responseType: "blob",
      });
      const mime = fmt === "csv" ? "text/csv"
        : fmt === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Land_Acquisition_cost_report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download report"); }
  };

  const downloadTax = async (fmt) => {
    try {
      const res = await api.get(`/reports/tax-report.${fmt}`, { responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `taxes_paid_report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download tax report"); }
  };

  if (!data) return <div className="p-8 label-caps animate-pulse">Loading…</div>;

  const spentPct = data.total_budget > 0 ? Math.round((data.total_actual / data.total_budget) * 100) : 0;

  return (
    <div>
      <PageHeader label="Overview" title="Dashboard">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-none" data-testid="portfolio-report-button">
              <DownloadSimple size={16} className="mr-1" /> Portfolio report
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none w-52">
            <DropdownMenuLabel className="label-caps text-xs">Full cost report</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => downloadPortfolio("pdf")} data-testid="portfolio-report-pdf" className="cursor-pointer">
              <FilePdf size={16} className="mr-2" /> Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadPortfolio("csv")} data-testid="portfolio-report-csv" className="cursor-pointer">
              <DownloadSimple size={16} className="mr-2" /> Download CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadPortfolio("xlsx")} data-testid="portfolio-report-xlsx" className="cursor-pointer">
              <DownloadSimple size={16} className="mr-2" /> Download Excel (multi-sheet)
            </DropdownMenuItem>
            {isManager && <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPortfolioEmailOpen(true)} data-testid="portfolio-report-email" className="cursor-pointer">
                <PaperPlaneTilt size={16} className="mr-2" /> Email report…
              </DropdownMenuItem>
            </>}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-none" data-testid="tax-report-button">
              <Receipt size={16} className="mr-1" /> Tax report
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none w-52">
            <DropdownMenuLabel className="label-caps text-xs">Taxes paid (all invoices)</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => downloadTax("pdf")} data-testid="tax-report-pdf" className="cursor-pointer">
              <FilePdf size={16} className="mr-2" /> Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadTax("csv")} data-testid="tax-report-csv" className="cursor-pointer">
              <DownloadSimple size={16} className="mr-2" /> Download CSV
            </DropdownMenuItem>
            {isManager && <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setTaxEmailOpen(true)} data-testid="tax-report-email" className="cursor-pointer">
                <PaperPlaneTilt size={16} className="mr-2" /> Email report…
              </DropdownMenuItem>
            </>}
          </DropdownMenuContent>
        </DropdownMenu>
        {isManager && (
          <Button variant="outline" onClick={sendDigest} disabled={sending} className="rounded-none" data-testid="send-digest-button">
            {sending ? "Sending…" : "Email weekly summary"}
          </Button>
        )}
      </PageHeader>
      <div className="p-4 sm:p-8">
        <OnboardingChecklist />
        <InviteTeammateNudge />
        {(() => {
          const hasLand = data.land_cost && (data.land_cost.actual > 0 || data.land_cost.budget > 0);
          const cardCount = 4 + (data.portfolio_health ? 1 : 0) + (hasLand ? 1 : 0);
          const gridCols = { 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" }[cardCount] || "lg:grid-cols-4";
          const landActual = data.land_cost?.actual > 0;
          return (
          <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} border-t border-l border-border mb-8 stagger`}>
            {data.portfolio_health && (
              <Stat label="Portfolio Health" value={data.portfolio_health.score} sub={data.portfolio_health.grade}
                icon={Gauge} danger={["Watch", "At Risk"].includes(data.portfolio_health.grade)} testId="portfolio-health-stat" />
            )}
            <Stat label="Total Budget" value={money(data.total_budget)} sub={`${data.project_count} projects`} icon={Wallet} />
            <Stat label="Total Spent" value={money(data.total_actual)} sub={`${spentPct}% of budget`} icon={TrendUp} />
            <Stat label="Remaining" value={money(data.remaining)} sub={data.remaining < 0 ? "Over budget" : "Under budget"} icon={TrendDown} danger={data.remaining < 0} />
            {hasLand && (
              <Stat label="Land Cost" testId="land-cost-stat" onClick={openLandItems}
                value={money(landActual ? data.land_cost.actual : data.land_cost.budget)}
                sub={landActual ? `${data.land_cost.pct_of_actual}% of actual spend` : `${data.land_cost.pct_of_budget}% of budget`}
                icon={MapPin} />
            )}
            <Stat label="Over Budget" value={data.over_budget_count} sub="projects flagged" icon={Warning} danger={data.over_budget_count > 0} />
          </div>
          );
        })()}

        {data.portfolio_health && (
          <div className="border border-border mb-8 reveal" data-testid="health-trend-card">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary">
              <Gauge size={18} weight="bold" />
              <span className="font-heading font-bold">Portfolio Health Trend</span>
              <span className="text-xs text-muted-foreground ml-1">daily snapshots</span>
            </div>
            {(data.health_trend?.length || 0) < 2 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="health-trend-empty">
                The trend line will appear here as daily health snapshots accumulate. Check back over the coming days.
              </p>
            ) : (
              <div className="p-4 sm:p-6">
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={data.health_trend} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="healthGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} minTickGap={20} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={28} />
                    <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontSize: 12 }}
                      formatter={(v) => [`${v}/100`, "Health"]} />
                    <Area type="monotone" dataKey="score" stroke="#059669" strokeWidth={2} fill="url(#healthGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {tax && tax.invoice_count > 0 && (
          <div className="border border-border mb-8 reveal grid grid-cols-1 sm:grid-cols-3" data-testid="taxes-paid-card">
            <div className="p-6 border-b sm:border-b-0 sm:border-r border-border flex items-center gap-3 bg-secondary">
              <Receipt size={20} weight="bold" />
              <div>
                <p className="label-caps">Taxes Paid to Date</p>
                <div className="font-mono-fig text-3xl font-semibold tabular-nums" data-testid="taxes-paid-total">{money(tax.total_tax)}</div>
              </div>
            </div>
            <div className="p-6 border-b sm:border-b-0 sm:border-r border-border">
              <p className="label-caps mb-2">Total Invoiced</p>
              <div className="font-mono-fig text-2xl font-semibold tabular-nums">{money(tax.total_invoiced)}</div>
            </div>
            <div className="p-6">
              <p className="label-caps mb-2">Invoices Read</p>
              <div className="font-mono-fig text-2xl font-semibold tabular-nums">{tax.invoice_count}</div>
              <div className="text-xs text-muted-foreground mt-1">AI-extracted sales/GST tax</div>
            </div>
          </div>
        )}

        {data.portfolio?.projects_analyzed > 0 && (
          <div className="border border-border mb-8 reveal" data-testid="portfolio-profit">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary">
              <ChartLineUp size={18} weight="bold" />
              <span className="font-heading font-bold">Portfolio Profit — If Sold</span>
              <span className="text-xs text-muted-foreground ml-1">({data.portfolio.projects_analyzed} projects with sale prices)</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-border">
              <PortStat label="Projected Sales" value={money(data.portfolio.total_sale_value)} />
              <PortStat label="Build + Selling Costs" value={money(data.portfolio.total_build_cost + data.portfolio.total_selling_costs)} />
              <PortStat label="Projected Net Profit" value={money(data.portfolio.total_net_profit)} accent={data.portfolio.total_net_profit >= 0 ? "text-emerald-700" : "text-destructive"} />
              <PortStat label="Avg Margin" value={`${data.portfolio.avg_margin.toFixed(1)}%`} last />
            </div>
            <div className="divide-y divide-border">
              {data.portfolio.projects.map((p) => (
                <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)} data-testid={`portfolio-project-${p.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-secondary transition-colors duration-200 gap-3">
                  <span className="font-medium truncate">{p.name}</span>
                  <span className="flex items-center gap-4 shrink-0">
                    <span className={`text-xs font-mono-fig ${p.margin >= 0 ? "text-muted-foreground" : "text-destructive"}`}>{p.margin.toFixed(1)}%</span>
                    <span className={`font-mono-fig tabular-nums ${p.net_profit >= 0 ? "text-emerald-700" : "text-destructive"}`}>
                      {p.net_profit < 0 ? "-" : ""}{money(Math.abs(p.net_profit))}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {data.over_budget_projects.length > 0 && (
          <div data-testid="over-budget-alert" className="border border-destructive mb-8 reveal">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-destructive bg-destructive/5">
              <Warning size={18} weight="bold" className="text-destructive" />
              <span className="font-semibold text-destructive text-sm">Variance Alerts</span>
            </div>
            <div className="divide-y divide-border">
              {data.over_budget_projects.map((p) => (
                <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                  data-testid={`alert-project-${p.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-secondary transition-colors duration-200">
                  <span className="font-medium">{p.name}</span>
                  <span className="font-mono-fig text-destructive tabular-nums">+{money(p.over_by)} over</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {data.at_risk && data.at_risk.length > 0 && (
          <div data-testid="budget-health-banner" className="border border-amber-500 mb-8 reveal">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-500 bg-amber-50">
              <Warning size={18} weight="bold" className="text-amber-600" />
              <span className="font-semibold text-amber-700 text-sm">Budget Health — Costs to Watch</span>
              <span className="text-xs text-muted-foreground ml-1">at or above {data.alert_threshold_pct || 90}% of budget</span>
            </div>
            <div className="divide-y divide-border">
              {data.at_risk.map((o) => {
                const over = o.status === "over";
                const c = over ? "text-destructive" : "text-amber-600";
                const border = over ? "border-destructive text-destructive hover:bg-destructive" : "border-amber-500 text-amber-600 hover:bg-amber-500";
                return (
                  <div key={o.id} data-testid={`overrun-${o.id}`}
                    className={`flex items-center justify-between gap-3 px-4 py-3 text-sm transition-colors duration-200 ${over ? "bg-destructive/5" : "hover:bg-secondary"}`}>
                    <button onClick={() => navigate(`/projects/${o.project_id}`)} className="flex-1 min-w-0 text-left">
                      <span className="font-medium block truncate flex items-center gap-1.5">
                        <Warning size={13} weight="fill" className={`${c} shrink-0`} />{o.description || o.category}
                      </span>
                      <span className="text-xs text-muted-foreground">{o.project_name} · {o.category} · <span className="font-mono-fig">{money(o.actual)}</span> of <span className="font-mono-fig">{money(o.budget)}</span></span>
                    </button>
                    <span className={`font-mono-fig tabular-nums font-medium shrink-0 ${c}`}>
                      {over ? `+${money(o.over_by)}` : `${o.pct}%`}
                    </span>
                    <button onClick={() => navigate(`/projects/${o.project_id}?item=${o.id}&view=receipts`)}
                      data-testid={`overrun-invoice-${o.id}`}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 border transition-colors hover:text-white shrink-0 ${border}`}>
                      <CurrencyDollar size={12} weight="bold" /> Reconcile
                    </button>
                    <button onClick={() => acknowledgeAlert(o.id)} data-testid={`overrun-ack-${o.id}`} title="Mark as reviewed"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors shrink-0">
                      <Check size={12} weight="bold" /> Reviewed
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {((data.allocation_drift && data.allocation_drift.length > 0) || (data.allocation_drift_budget && data.allocation_drift_budget.length > 0)) && (() => {
          const driftList = driftBasis === "actual" ? (data.allocation_drift || []) : (data.allocation_drift_budget || []);
          return (
          <div data-testid="allocation-drift-banner" className="border border-indigo-400 mb-8 reveal">
            <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-indigo-400 bg-indigo-50">
              <ArrowsLeftRight size={18} weight="bold" className="text-indigo-600" />
              <span className="font-semibold text-indigo-700 text-sm">Allocation Drift — Categories outside typical range</span>
              <span className="text-xs text-muted-foreground ml-1">{driftBasis === "actual" ? "actual spend" : "budget"} share vs industry-standard %</span>
              <div className="ml-auto inline-flex border border-indigo-300 text-xs" data-testid="drift-basis-toggle">
                {["actual", "budget"].map((b) => (
                  <button key={b} onClick={() => setDriftBasis(b)} data-testid={`drift-basis-${b}`}
                    className={`px-3 py-1 capitalize transition-colors duration-200 ${driftBasis === b ? "bg-indigo-600 text-white" : "text-indigo-700 hover:bg-indigo-100"}`}>
                    {b}
                  </button>
                ))}
              </div>
            </div>
            {driftList.length === 0 ? (
              <p className="px-4 py-4 text-sm text-muted-foreground" data-testid="drift-empty">
                No {driftBasis} allocation drift — {driftBasis === "actual" ? "projects underway are spending in line with typical ranges." : "budgets are allocated in line with typical ranges."}
              </p>
            ) : (
            <div className="divide-y divide-border">
              {driftList.map((d) => {
                const over = d.status === "over";
                const c = over ? "text-rose-600" : "text-blue-600";
                const key = `${d.project_id}-${d.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
                return (
                  <div key={key} data-testid={`drift-${key}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-secondary transition-colors duration-200">
                    <button onClick={() => navigate(`/projects/${d.project_id}`)} className="flex-1 min-w-0 text-left">
                      <span className="font-medium block truncate">{d.category}</span>
                      <span className="text-xs text-muted-foreground">
                        {d.project_name} · {d.group} · <span className="font-mono-fig">{money(d.amount)}</span> {driftBasis === "actual" ? "spent" : "budgeted"}
                      </span>
                    </button>
                    <div className="text-right shrink-0">
                      <span className={`font-mono-fig tabular-nums font-semibold ${c}`}>{d.pct}%</span>
                      <span className="block text-[11px] text-muted-foreground">typical {d.typical_min}–{d.typical_max}%</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 border shrink-0 ${over ? "border-rose-400 text-rose-600" : "border-blue-400 text-blue-600"}`}
                      data-testid={`drift-status-${key}`}>
                      {over ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                      {over ? "Over-allocated" : "Under-allocated"} +{d.drift}%
                    </span>
                  </div>
                );
              })}
            </div>
            )}
          </div>
          );
        })()}

        {data.by_project && data.by_project.length > 0 && (() => {
          const scored = data.by_project.filter((p) => p.health);
          const avg = scored.length ? Math.round(scored.reduce((s, p) => s + p.health.score, 0) / scored.length) : 0;
          const avgGrade = avg >= 85 ? "Excellent" : avg >= 70 ? "On Track" : avg >= 50 ? "Watch" : "At Risk";
          const attention = ["Watch", "At Risk"];
          const rows = [...scored]
            .filter((p) => (leaderFilter === "attention" ? attention.includes(p.health.grade) : true))
            .sort((a, b) => a.health.score - b.health.score);
          return (
          <div data-testid="health-leaderboard" className="border border-border mb-8 reveal">
            <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border bg-secondary">
              <Gauge size={18} weight="bold" />
              <span className="font-heading font-bold">Cost Health Leaderboard</span>
              <span className="text-xs text-muted-foreground ml-1">riskiest projects first</span>
              <span className="text-xs ml-2 flex items-center gap-1.5" data-testid="leaderboard-avg">
                <span className="text-muted-foreground">Portfolio avg</span>
                <HealthBadge health={{ score: avg, grade: avgGrade }} />
              </span>
              <div className="ml-auto inline-flex border border-border text-xs" data-testid="leaderboard-filter">
                {[["all", "All"], ["attention", "Needs attention"]].map(([v, lbl]) => (
                  <button key={v} onClick={() => setLeaderFilter(v)} data-testid={`leaderboard-filter-${v}`}
                    className={`px-3 py-1 transition-colors duration-200 ${leaderFilter === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            {rows.length === 0 ? (
              <p className="px-4 py-4 text-sm text-muted-foreground" data-testid="leaderboard-empty">
                No projects need attention — all are On Track or better.
              </p>
            ) : (
            <div className="divide-y divide-border">
              {rows.map((p) => (
                <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                  data-testid={`leaderboard-row-${p.id}`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm text-left hover:bg-secondary transition-colors duration-200">
                  <span className="font-medium truncate flex-1 min-w-0">{p.name}</span>
                  <span className="text-xs text-muted-foreground font-mono-fig tabular-nums shrink-0">
                    {money(p.actual)} / {money(p.budget)}
                  </span>
                  <HealthBadge health={p.health} testId={`leaderboard-health-${p.id}`} className="shrink-0" />
                </button>
              ))}
            </div>
            )}
          </div>
          );
        })()}

        {(() => {
          const risks = (data.by_category || [])
            .filter((c) => (c.budget || 0) > 0 && (c.quoted || 0) > c.budget)
            .map((c) => ({ ...c, over: c.quoted - c.budget, pct: Math.round(((c.quoted - c.budget) / c.budget) * 100) }))
            .sort((a, b) => b.over - a.over);
          if (risks.length === 0) return null;
          const totalOver = risks.reduce((s, r) => s + r.over, 0);
          return (
            <div className="border border-destructive/40 bg-destructive/5 reveal" data-testid="overrun-risk-widget">
              <div className="flex items-center gap-2 px-6 py-4 border-b border-destructive/30">
                <Warning size={18} weight="fill" className="text-destructive" />
                <span className="text-sm font-semibold text-destructive">Quote Overrun Risk</span>
                <span className="text-xs text-muted-foreground ml-1">
                  {risks.length} categor{risks.length === 1 ? "y" : "ies"} with accepted quotes above budget · {money(totalOver)} over
                </span>
              </div>
              <div className="divide-y divide-destructive/15">
                {risks.map((r) => (
                  <div key={r.category} data-testid={`overrun-row-${r.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className="w-full flex items-center gap-3 px-6 py-3 text-left">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.category}</p>
                      <p className="text-xs text-muted-foreground">
                        Budget <span className="font-mono-fig">{money(r.budget)}</span> · Quoted <span className="font-mono-fig text-amber-600">{money(r.quoted)}</span>
                      </p>
                    </div>
                    <span className="text-sm font-mono-fig font-semibold text-destructive whitespace-nowrap shrink-0">
                      +{money(r.over)} <span className="text-xs">({r.pct}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 reveal">
          <div className="lg:col-span-2 border border-border p-6">
            <p className="label-caps mb-6">Budget vs Quoted vs Actual — by Project</p>
            {data.by_project.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={data.by_project} margin={{ left: 10, right: 10, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={60}
                    tickFormatter={(v) => (v && v.length > 12 ? `${v.slice(0, 12)}…` : v)} />
                  <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A" }} />
                  <Legend />
                  <Bar dataKey="budget" name="Budget" fill="#94a3b8" />
                  <Bar dataKey="quoted" name="Quoted" fill="#F59E0B" />
                  <Bar dataKey="actual" name="Actual" fill="#0A0A0A" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="border border-border p-6">
            <p className="label-caps mb-6">Actual Cost by Category</p>
            {data.by_category.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={data.by_category.filter((c) => c.actual > 0)} dataKey="actual" nameKey="category"
                    cx="50%" cy="50%" outerRadius={100}>
                    {data.by_category.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {data.by_group && (
          <div className="mt-6 reveal">
            <HardSoftBreakdown data={data.by_group} categories={data.by_category} testId="dashboard-hard-soft" />
          </div>
        )}

        {isManager && emailLog?.recent?.length > 0 && (
          <div className="border border-border mt-6 reveal" data-testid="report-send-history">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <PaperPlaneTilt size={18} weight="bold" />
                <p className="label-caps">Report Send History</p>
              </div>
              {emailLog.recent.length > 5 && (
                <button onClick={() => setShowSendHistory((v) => !v)} data-testid="send-history-toggle"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  {showSendHistory ? "Show less" : `View all (${emailLog.recent.length})`}
                </button>
              )}
            </div>
            <div className="divide-y divide-border">
              {(showSendHistory ? emailLog.recent : emailLog.recent.slice(0, 5)).map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-6 py-3" data-testid={`send-history-row-${i}`}>
                  <div className={r.sent > 0 ? "text-emerald-700" : "text-muted-foreground"}>
                    <PaperPlaneTilt size={16} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{REPORT_KIND_LABEL[r.kind] || r.kind}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {r.recipients?.length ? r.recipients.join(", ") : "—"}
                    </p>
                  </div>
                  <span className={`text-xs font-mono-fig shrink-0 ${r.sent > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>
                    {r.sent}/{r.total} sent
                  </span>
                  <span className="text-xs text-muted-foreground font-mono-fig whitespace-nowrap shrink-0">{timeAgo(r.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-border mt-6 reveal" data-testid="dashboard-recent-activity">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="bold" />
              <p className="label-caps">Recent Activity</p>
            </div>
            <button onClick={() => navigate("/activity")} data-testid="view-all-activity"
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors duration-200">
              View all <ArrowRight size={14} />
            </button>
          </div>
          {activity.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground">No activity yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {activity.map((l) => {
                const Icon = ACT_ICON(l.action);
                return (
                  <div key={l.id} className="flex items-center gap-3 px-6 py-3">
                    <div className={ACT_COLOR(l.action)}><Icon size={18} weight="bold" /></div>
                    <p className="flex-1 text-sm min-w-0 truncate">
                      <span className="font-semibold">{l.actor_name}</span>{" "}
                      <span className="text-muted-foreground">{l.action} {l.entity_type}</span>{" "}
                      {l.entity_name && <span className="font-medium">"{l.entity_name}"</span>}
                    </p>
                    <span className="font-mono-fig text-xs text-muted-foreground whitespace-nowrap">{timeAgo(l.created_at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={showLandItems} onOpenChange={setShowLandItems}>
        <DialogContent className="max-w-3xl rounded-none" data-testid="land-items-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <MapPin size={18} weight="bold" />
                <DialogTitle>Land Acquisition — Line Items</DialogTitle>
              </div>
              {landItems && landItems.length > 0 && (
                <div className="flex items-center gap-2 mr-8">
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="land-email"
                    onClick={() => setLandEmailOpen(true)}>
                    <PaperPlaneTilt size={14} className="mr-1" /> Email
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="land-export-pdf"
                    onClick={() => downloadCategoryItems("pdf")}>
                    <FilePdf size={14} className="mr-1" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="land-export-csv"
                    onClick={() => downloadCategoryItems("csv")}>
                    <DownloadSimple size={14} className="mr-1" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="land-export-xlsx"
                    onClick={() => downloadCategoryItems("xlsx")}>
                    <DownloadSimple size={14} className="mr-1" /> Excel
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          {landItems === null ? (
            <div className="py-8 text-sm text-muted-foreground animate-pulse">Loading land items…</div>
          ) : landItems.length === 0 ? (
            <div className="py-8 text-sm text-muted-foreground" data-testid="land-items-empty">No land acquisition line items found.</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary sticky top-0">
                  <tr className="text-left label-caps">
                    <th className="px-3 py-2 font-medium">Project</th>
                    <th className="px-3 py-2 font-medium">Description</th>
                    <th className="px-3 py-2 font-medium">Vendor</th>
                    <th className="px-3 py-2 font-medium text-right">Budget</th>
                    <th className="px-3 py-2 font-medium text-right">Actual</th>
                    <th className="px-3 py-2 font-medium text-right">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {landItems.map((it) => {
                    const variance = (it.actual_amount || 0) - (it.budget_amount || 0);
                    return (
                      <tr key={it.id} data-testid={`land-item-${it.id}`}
                        onClick={() => { setShowLandItems(false); navigate(`/projects/${it.project_id}`); }}
                        className="cursor-pointer hover:bg-secondary/50 transition-colors duration-150">
                        <td className="px-3 py-2 font-medium">{it.project_name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{it.description || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{it.vendor_name || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono-fig tabular-nums">{money(it.budget_amount)}</td>
                        <td className="px-3 py-2 text-right font-mono-fig tabular-nums">{money(it.actual_amount)}</td>
                        <td className={`px-3 py-2 text-right font-mono-fig tabular-nums ${variance > 0 ? "text-destructive" : variance < 0 ? "text-emerald-700" : "text-muted-foreground"}`}>
                          {variance > 0 ? "+" : ""}{money(variance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EmailReportDialog open={landEmailOpen} onOpenChange={setLandEmailOpen} categoryName="Land Acquisition" onSent={loadEmailLog} />

      <EmailReportDialog open={portfolioEmailOpen} onOpenChange={setPortfolioEmailOpen}
        title="Portfolio cost" endpoint="/reports/email-portfolio" extraPayload={{}} onSent={loadEmailLog} />

      <EmailReportDialog open={taxEmailOpen} onOpenChange={setTaxEmailOpen}
        title="Taxes paid" endpoint="/reports/email-tax" extraPayload={{}} onSent={loadEmailLog} />
    </div>
  );
}

function Empty() {
  return (
    <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">
      No cost data yet. Add projects and line items to see charts.
    </div>
  );
}

function PortStat({ label, value, accent, last }) {
  return (
    <div className={`p-4 border-r border-border ${last ? "md:border-r-0" : ""}`}>
      <div className="label-caps mb-1">{label}</div>
      <div className={`font-mono-fig text-lg font-semibold tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}
